import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import type {
  ProjectListResponse,
  ProjectDetail,
  BudgetResponse,
  CashFlowResponse,
  TakeoffItem,
  ActualCostEntry,
  VarianceResponse,
  ImportResult,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── Wait for Firebase auth to be fully restored before sending requests ──
//
// Firebase restores auth state from IndexedDB asynchronously after page load.
// The first `onAuthStateChanged` callback may fire with `null` before the
// persisted session is restored.  We need to wait until Firebase has
// *definitively* settled on the auth state.
//
// Strategy: we track auth readiness with a promise that resolves once
// `onAuthStateChanged` fires with a *user* OR after a generous timeout
// (2 seconds) if it fires with null (meaning there genuinely is no session).

let _authReadyResolved = false;
let _resolveAuthReady: (() => void) | null = null;

const _authReady = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve;
});

// Start listening immediately at module load time
onAuthStateChanged(auth, (user) => {
  if (user) {
    _authReadyResolved = true;
    _resolveAuthReady?.();
    _resolveAuthReady = null;
  } else if (!_authReadyResolved) {
    // Got null — could be initial state before restore, or genuinely signed out.
    // Wait a short time for a second callback with the real user.
    setTimeout(() => {
      if (!_authReadyResolved) {
        _authReadyResolved = true;
        _resolveAuthReady?.();
        _resolveAuthReady = null;
      }
    }, 2000);
  }
});

// Attach Firebase ID token to every request
api.interceptors.request.use(async (config) => {
  if (!_authReadyResolved) {
    await _authReady;
  }
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Retry with exponential backoff on 5xx / network errors ──

function isRetryable(error: AxiosError): boolean {
  // Don't retry timeouts on POST — AI generation endpoints are slow by design
  if (error.code === 'ECONNABORTED' && error.config?.method === 'post') return false;
  // Network error (no response at all)
  if (!error.response) return true;
  // Server errors (5xx) are transient
  if (error.response.status >= 500) return true;
  // Request timeout on GET requests
  if (error.code === 'ECONNABORTED') return true;
  return false;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;
    if (!config) return Promise.reject(error);

    // ── Auth errors: don't retry, handle immediately ──
    if (error.response?.status === 401) {
      // Don't auto-sign-out on 401 — it can cause redirect loops when
      // Firebase auth state is still initialising.  Let the error bubble
      // up to the component which will show an error state.  If the user's
      // session is truly invalid, Firebase's own auth state listener in
      // AuthContext will detect it and show the login page.
      return Promise.reject(error);
    }
    if (error.response?.status === 403) {
      // 403 means the token was valid but the user isn't on the allowlist.
      // Sign out and show the access-denied message.
      if (auth.currentUser) {
        const detail = (error.response.data as any)?.detail;
        const message =
          (typeof detail === 'object' && detail?.message) ||
          (typeof detail === 'string' && detail) ||
          'Access denied. Your email is not on the approved list.';
        sessionStorage.setItem('auth_error', message);
        await auth.signOut();
        // Don't use window.location.href — let React handle the re-render
        // after signOut triggers onAuthStateChanged(null)
      }
      return Promise.reject(error);
    }

    // ── Retry logic for transient failures ──
    const retryCount = config._retryCount ?? 0;
    if (isRetryable(error) && retryCount < MAX_RETRIES) {
      config._retryCount = retryCount + 1;
      const backoff = BASE_DELAY_MS * Math.pow(2, retryCount); // 1s, 2s, 4s
      console.warn(
        `[api] Retry ${config._retryCount}/${MAX_RETRIES} for ${config.method?.toUpperCase()} ${config.url} in ${backoff}ms`,
      );
      await delay(backoff);
      return api.request(config);
    }

    return Promise.reject(error);
  },
);

// ── Projects ──

export async function listProjects(): Promise<ProjectListResponse> {
  const { data } = await api.get<ProjectListResponse>('/projects');
  return data;
}

export async function getProject(id: number): Promise<ProjectDetail> {
  const { data } = await api.get<ProjectDetail>(`/projects/${id}`);
  return data;
}

export async function getBudget(id: number): Promise<BudgetResponse> {
  const { data } = await api.get<BudgetResponse>(`/projects/${id}/budget`);
  return data;
}

export async function getCashFlow(id: number): Promise<CashFlowResponse> {
  const { data } = await api.get<CashFlowResponse>(`/projects/${id}/cashflow`);
  return data;
}

export async function getTakeoff(id: number): Promise<TakeoffItem[]> {
  const { data } = await api.get<TakeoffItem[]>(`/projects/${id}/takeoff`);
  return data;
}

export async function getActuals(id: number): Promise<ActualCostEntry[]> {
  const { data } = await api.get<ActualCostEntry[]>(`/projects/${id}/actuals`);
  return data;
}

export async function getVariance(id: number): Promise<VarianceResponse> {
  const { data } = await api.get<VarianceResponse>(`/projects/${id}/variance`);
  return data;
}

// ── Import ──

export async function importFile(file: File, name?: string): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  if (name) form.append('name', name);
  const { data } = await api.post<ImportResult>('/projects/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ── Link Actuals ──

export async function linkActuals(projectId: number, actualsProjectId: number): Promise<void> {
  await api.post(`/projects/${projectId}/link-actuals`, null, {
    params: { actuals_project_id: actualsProjectId },
  });
}

export default api;
